




import { useEffect, useRef } from "react";
import { useToast } from "@chakra-ui/react";
import { useColor } from "../ui/ColorContext";


type Props = {
  onUnlock: () => void;
  questId: number;
};

const HologramGateQuest1 = ({ onUnlock, questId }: Props) => {
  const { primaryColor } = useColor();

  const hexToRgb = (hex: string): string => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result) return "0, 255, 187";
    const r = Number.parseInt(result[1], 16);
    const g = Number.parseInt(result[2], 16);
    const b = Number.parseInt(result[3], 16);
    return `${r}, ${g}, ${b}`;
  };

  const toast = useToast();
  const sceneRef = useRef<HTMLDivElement>(null);
  const hitRef = useRef<SVGRectElement>(null);
  const holoVisualsRef = useRef<SVGGElement>(null);
  const fxRef = useRef<SVGGElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);

  /* ================= SOUND (INTEGRATED ONLY) ================= */
  const audioUnlockedRef = useRef(false);

  const unlockAudio = () => {
    if (audioUnlockedRef.current) return;
    audioUnlockedRef.current = true;
    window.removeEventListener("click", unlockAudio);
    window.removeEventListener("touchstart", unlockAudio);
  };

  const playNotificationSound = () => {
    if (!audioUnlockedRef.current) return;

    const ctx = new (window.AudioContext ||
      (window as any).webkitAudioContext)();
    const now = ctx.currentTime;

    const tap = (t: number) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(1500, t);
      gain.gain.setValueAtTime(0.04, t);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.05);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(t);
      osc.stop(t + 0.05);
    };

    tap(now);
    tap(now + 0.08);
  };

  const playUnlockSound = () => {
    const ctx = new (window.AudioContext ||
      (window as any).webkitAudioContext)();
    const now = ctx.currentTime;

    const tick = ctx.createOscillator();
    const tickGain = ctx.createGain();
    tick.type = "square";
    tick.frequency.setValueAtTime(1800, now);
    tickGain.gain.setValueAtTime(0.05, now);
    tickGain.gain.exponentialRampToValueAtTime(0.001, now + 0.02);
    tick.connect(tickGain);
    tickGain.connect(ctx.destination);

    const chirp = ctx.createOscillator();
    const chirpGain = ctx.createGain();
    chirp.type = "sine";
    chirp.frequency.setValueAtTime(400, now + 0.02);
    chirp.frequency.exponentialRampToValueAtTime(1200, now + 0.15);
    chirpGain.gain.setValueAtTime(0, now);
    chirpGain.gain.linearRampToValueAtTime(0.1, now + 0.05);
    chirpGain.gain.exponentialRampToValueAtTime(0.001, now + 0.2);
    chirp.connect(chirpGain);
    chirpGain.connect(ctx.destination);

    const hum = ctx.createOscillator();
    const humGain = ctx.createGain();
    hum.type = "triangle";
    hum.frequency.setValueAtTime(120, now + 0.1);
    humGain.gain.setValueAtTime(0, now + 0.1);
    humGain.gain.linearRampToValueAtTime(0.05, now + 0.2);
    humGain.gain.exponentialRampToValueAtTime(0.001, now + 0.5);
    hum.connect(humGain);
    humGain.connect(ctx.destination);

    tick.start(now);
    tick.stop(now + 0.02);
    chirp.start(now + 0.02);
    chirp.stop(now + 0.2);
    hum.start(now + 0.1);
    hum.stop(now + 0.5);
  };
  /* ============================================================ */

  // HOLOGRAM POSITION
  // const HOLO_X = 595;
  // const HOLO_Y = 490;
  // const HOLO_W = 210;
  // const HOLO_H = 150;
  // const CORNER_OFFSET = 10;
  const HOLO_X = 638;
const HOLO_Y = 490;
const HOLO_W = 210;
const HOLO_H = 150;
const CORNER_OFFSET = 10;

  /* INTRO TOAST */
  useEffect(() => {
    const id = "quest1-intro-toast";

    if (!toast.isActive(id)) {
      toast({
        id,
        duration: 2000,
        isClosable: true,
        position: "top",
        render: () => (
          <div
            style={{
              background: "rgba(10, 15, 30, 0.9)",
              color: primaryColor,
              border: `2px solid ${primaryColor}`,
              backdropFilter: "blur(6px)",
              padding: "12px 16px",
              borderRadius: "10px",
              marginLeft: "-50px",
              fontWeight: 600,
              boxShadow: `
                0 0 10px rgba(${hexToRgb(primaryColor)}, 0.8),
                0 0 25px rgba(${hexToRgb(primaryColor)}, 0.5),
                inset 0 0 10px rgba(${hexToRgb(primaryColor)}, 0.4)
              `,
            }}
          >
            To proceed further, please click on the hologram.
          </div>
        ),
      });

      setTimeout(() => {
        playNotificationSound();
      }, 850);
    }
  }, []);

  /* AUDIO UNLOCK */
  useEffect(() => {
    window.addEventListener("click", unlockAudio);
    window.addEventListener("touchstart", unlockAudio);
    return () => {
      window.removeEventListener("click", unlockAudio);
      window.removeEventListener("touchstart", unlockAudio);
    };
  }, []);

  const spawnRipple = (e: MouseEvent) => {
    const svg = sceneRef.current?.querySelector("svg");
    const fx = fxRef.current;
    if (!svg || !fx) return;

    const pt = (svg as SVGSVGElement).createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;

    const svgP = pt.matrixTransform(
      (svg as SVGSVGElement).getScreenCTM()!.inverse()
    );

    const circle = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "circle"
    );

    circle.setAttribute("cx", String(svgP.x));
    circle.setAttribute("cy", String(svgP.y));
    circle.setAttribute("r", "1");
    circle.setAttribute("fill", "none");
    circle.setAttribute("stroke", "#ffffff");
    circle.setAttribute("stroke-width", "4");
    circle.style.transition = "all 0.6s cubic-bezier(0.16, 1, 0.3, 1)";

    fx.appendChild(circle);

    requestAnimationFrame(() => {
      circle.setAttribute("r", "200");
      circle.style.opacity = "0";
    });

    setTimeout(() => circle.remove(), 600);
  };

   function playOverviewHologram(ctx: AudioContext) {
    const now = ctx.currentTime;
    [1320, 1760, 2640].forEach((freq, i) => {
      const t = now + i * 0.05;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, t);
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(0.06, t + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.4);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(t);
      osc.stop(t + 0.42);
    });
  }

  useEffect(() => {
    const audioCtx = new AudioContext();

    playOverviewHologram(audioCtx);

    return () => {
      audioCtx.close();
    };
  }, []);
  
  useEffect(() => {
    const hit = hitRef.current;
    const holoVisuals = holoVisualsRef.current;

    if (!hit || !holoVisuals) return;

    let isClicked = false;

    const handleClick = (e: MouseEvent) => {
      toast.close("quest1-intro-toast");

      if (isClicked) return;
      isClicked = true;

      playUnlockSound();
      spawnRipple(e);
      holoVisuals.classList.add("is-active");

      const id = "quest1-access-toast";

      if (!toast.isActive(id)) {
        toast({
          id,
          duration: 2500,
          isClosable: true,
          position: "top",
          containerStyle: { marginTop: "40px", marginLeft: "-120px" },
          render: () => {
            playNotificationSound();
            return (
              <div
                style={{
                  background: "rgba(10, 15, 30, 0.9)",
                  color: primaryColor,
                  border: `2px solid ${primaryColor}`,
                  backdropFilter: "blur(6px)",
                  padding: "14px 18px",
                  borderRadius: "10px",
                  fontWeight: 600,
                  boxShadow: `
                    0 0 12px rgba(${hexToRgb(primaryColor)}, 0.9),
                    0 0 30px rgba(${hexToRgb(primaryColor)}, 0.6),
                    inset 0 0 12px rgba(${hexToRgb(primaryColor)}, 0.4)
                  `,
                }}
              >
                Accessing mission intel… here is a brief overview to help you
                prepare.
              </div>
            );
          },
        });
      }

      setTimeout(() => onUnlock(), 900);
    };

    hit.addEventListener("click", handleClick);
    return () => hit.removeEventListener("click", handleClick);
  }, [onUnlock]);

  return (
    <div className="scene" ref={sceneRef}>
      <svg
        className="overlay"
        viewBox="0 0 2048 1170"
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <filter
            id="holoGlowStrong"
            x="-50%"
            y="-50%"
            width="200%"
            height="200%"
          >
            <feGaussianBlur stdDeviation="8" result="blur" />
            <feColorMatrix
              in="blur"
              type="matrix"
              values="0 0 0 0 0
                      0 1 0 0 0.8
                      0 0 1 0 1
                      0 0 0 1 0"
            />
            <feMerge>
              <feMergeNode />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          <linearGradient id="scanGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(0,210,255,0)" />
            <stop offset="50%" stopColor="rgba(0,210,255,0.4)" />
            <stop offset="100%" stopColor="rgba(0,210,255,0)" />
          </linearGradient>
{/* 
          <clipPath id="clipHolo">
            <rect
              x={HOLO_X}
              y={HOLO_Y}
              width={HOLO_W}
              height={HOLO_H}
              rx="12"
              ry="12"
            />
          </clipPath> */}

          <clipPath id="clipHolo1">
  <rect
    x={HOLO_X}
    y={HOLO_Y}
    width={HOLO_W}
    height={HOLO_H}
    rx="12"
    ry="12"
  />
</clipPath>
        </defs>

        <rect
          ref={hitRef}
          className="holo-hit"
          x={HOLO_X}
          y={HOLO_Y}
          width={HOLO_W}
          height={HOLO_H}
          rx="12"
          ry="12"
        />
        <text
  x={HOLO_X + HOLO_W / 2}
  y={HOLO_Y + HOLO_H / 2}
  textAnchor="middle"
  dominantBaseline="middle"
  fontSize="16"
  letterSpacing="2"
   fontWeight="bold"
  style={{
    fill: 'Black',
    fontFamily: "Orbitron, sans-serif",
    // filter: "drop-shadow(0 0 6px cyan)"
  }}
>
  CLICK HERE

</text>

        <g ref={holoVisualsRef} className="holo-group holo-flicker">
          <rect
            x={HOLO_X}
            y={HOLO_Y}
            width={HOLO_W}
            height={HOLO_H}
            rx="12"
            ry="12"
            className="holo-stroke_new"
          />

          <g className="holo-corners">
            {/* <path
              d={`M ${HOLO_X + CORNER_OFFSET} ${
                HOLO_Y + CORNER_OFFSET
              } h 30
                  M ${HOLO_X + CORNER_OFFSET} ${
                HOLO_Y + CORNER_OFFSET
              } v 30`}
            />
            <path
              d={`M ${HOLO_X + HOLO_W - CORNER_OFFSET} ${
                HOLO_Y + CORNER_OFFSET
              } h -30
                  M ${HOLO_X + HOLO_W - CORNER_OFFSET} ${
                HOLO_Y + CORNER_OFFSET
              } v 30`}
            />
            <path
              d={`M ${HOLO_X + CORNER_OFFSET} ${
                HOLO_Y + HOLO_H - CORNER_OFFSET
              } h 30
                  M ${HOLO_X + CORNER_OFFSET} ${
                HOLO_Y + HOLO_H - CORNER_OFFSET
              } v -30`}
            />
            <path
              d={`M ${HOLO_X + HOLO_W - CORNER_OFFSET} ${
                HOLO_Y + HOLO_H - CORNER_OFFSET
              } h -30
                  M ${HOLO_X + HOLO_W - CORNER_OFFSET} ${
                HOLO_Y + HOLO_H - CORNER_OFFSET
              } v -30`}
            /> */}

            
  <path d="M648 500 h30 M648 500 v30" />
  <path d="M838 500 h-30 M838 500 v30" />
  <path d="M648 630 h30 M648 630 v-30" />
  <path d="M838 630 h-30 M838 630 v-30" />

          </g>

          <rect
            className="scanline"
            x={HOLO_X}
            y={HOLO_Y}
            width={HOLO_W}
            height="40"
            fill="url(#scanGrad)"
            // clipPath="url(#clipHolo)"
            
clipPath="url(#clipHolo1)"
          />
        </g>

        <g ref={fxRef} />
      </svg>

      <div ref={tipRef} className="tip">
        <div className="kicker">Click Here</div>
      </div>
    </div>
  );
};

export default HologramGateQuest1;

